export const ABILITY_KEY_MAP = Object.freeze({
  strength: ['strength', 'str'],
  dexterity: ['dexterity', 'dex'],
  constitution: ['constitution', 'con'],
  intelligence: ['intelligence', 'int'],
  wisdom: ['wisdom', 'wis'],
  charisma: ['charisma', 'cha']
});

const NESTED_SOURCE_KEYS = [
  'entityPayload',
  'data',
  'canonicalData',
  'schemaData',
  'characterData',
  'monsterData',
  'entity',
  'compat',
  'originalData'
];

export const asRecord = (value) => (
  value && typeof value === 'object' ? value : null
);

export const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toSourceArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  return value == null ? [] : [value];
};

export const firstPresentValue = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }
    return value;
  }

  return undefined;
};

export const collectCombatantSources = (...inputs) => {
  const visited = new Set();
  const queue = [];
  const sources = [];

  const enqueue = (value) => {
    const record = asRecord(value);
    if (!record || visited.has(record)) return;
    visited.add(record);
    queue.push(record);
  };

  inputs.forEach((input) => {
    toSourceArray(input).forEach(enqueue);
  });

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;

    sources.push(current);

    NESTED_SOURCE_KEYS.forEach((key) => enqueue(current[key]));

    const originalUnifiedEntity = asRecord(current.originalUnifiedEntity);
    if (originalUnifiedEntity) {
      enqueue(originalUnifiedEntity);
      enqueue(originalUnifiedEntity.data);
    }
  }

  return sources;
};

export const parseChallengeRating = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const fractionMatch = trimmed.match(/^(\d+)\s*\/\s*(\d+)$/);
    if (fractionMatch) {
      const numerator = Number(fractionMatch[1]);
      const denominator = Number(fractionMatch[2]);
      if (denominator > 0) {
        return numerator / denominator;
      }
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value && typeof value === 'object') {
    return parseChallengeRating(
      value.value
      ?? value.cr
      ?? value.rating
      ?? value.challengeRating
      ?? value.display
      ?? null
    );
  }

  return null;
};

export const deriveProficiencyBonusFromChallengeRating = (challengeRating) => {
  const numericCr = parseChallengeRating(challengeRating);
  if (numericCr == null) return null;

  if (numericCr <= 4) return 2;
  if (numericCr <= 8) return 3;
  if (numericCr <= 12) return 4;
  if (numericCr <= 16) return 5;
  if (numericCr <= 20) return 6;
  if (numericCr <= 24) return 7;
  if (numericCr <= 28) return 8;
  return 9;
};

export const parseSpeedFeet = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const match = value.match(/(\d+(?:\.\d+)?)/);
    if (!match) return null;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value && typeof value === 'object') {
    const walk = value.walk ?? value.ground ?? value.land ?? value.base ?? value.value;
    if (walk != null) {
      return parseSpeedFeet(walk);
    }

    for (const nested of Object.values(value)) {
      const parsed = parseSpeedFeet(nested);
      if (parsed != null) {
        return parsed;
      }
    }
  }

  return null;
};

export const resolveSpellLevel = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;

    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }

  return 1;
};

const SPELL_LEVEL_ARRAY_KEYS = Array.from({ length: 10 }, (_, index) => [`level${index}`, String(index), index]);

const normalizeSpellNameKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[-_\s]+/g, ' ')
  .replace(/[^\w\s]/g, '')
  .trim();

const normalizeSpellEntry = (spell, defaultLevel = null) => {
  if (!spell) return null;

  const baseEntry = typeof spell === 'string'
    ? { name: spell, level: defaultLevel }
    : { ...spell };

  const name = firstPresentValue(baseEntry.name, baseEntry.id, baseEntry.title, baseEntry.label);
  if (!name) {
    return null;
  }

  const normalizedLevel = baseEntry.level ?? baseEntry.spellLevel ?? defaultLevel;

  return {
    ...baseEntry,
    name,
    level: normalizedLevel
  };
};

const pushUniqueSpellEntry = (target, spell, defaultLevel, seenKeys) => {
  const normalized = normalizeSpellEntry(spell, defaultLevel);
  if (!normalized) return;

  const key = normalizeSpellNameKey(normalized.name || normalized.id);
  if (!key || seenKeys.has(key)) return;

  seenKeys.add(key);
  target.push(normalized);
};

const mergeSpellArray = (target, candidate, defaultLevel, seenKeys) => {
  if (!Array.isArray(candidate)) return;
  candidate.forEach((spell) => pushUniqueSpellEntry(target, spell, defaultLevel, seenKeys));
};

const mergeSpellLevelMap = (target, candidate, seenKeysByLevel) => {
  if (!candidate || typeof candidate !== 'object') return;

  Object.entries(candidate).forEach(([levelKey, spellList]) => {
    const level = resolveSpellLevel(levelKey);
    if (!Array.isArray(spellList) || level < 0 || level > 9) return;

    target[level] = target[level] || [];
    seenKeysByLevel[level] = seenKeysByLevel[level] || new Set();
    mergeSpellArray(target[level], spellList, level, seenKeysByLevel[level]);
  });
};

const mergeSpellSlots = (target, candidate) => {
  if (!candidate || typeof candidate !== 'object') return;

  Object.entries(candidate).forEach(([levelKey, slotData]) => {
    const level = resolveSpellLevel(levelKey);
    if (level < 1 || level > 9) return;

    // collectCombatantSources is ordered from the live combat projection to its
    // nested canonical/original payloads. Preserve the first valid slot value so
    // an older embedded character snapshot cannot refund a slot that was spent
    // earlier in the same turn.
    // rule_reference: AGENTS.md - the session snapshot is the only mutable state.
    // date_of_change: 2026-08-20
    if (Object.prototype.hasOwnProperty.call(target, level)) return;

    if (typeof slotData === 'number' && Number.isFinite(slotData) && slotData >= 0) {
      target[level] = { total: slotData, used: 0 };
      return;
    }

    if (!slotData || typeof slotData !== 'object') return;

    const total = toFiniteNumber(slotData.total);
    const used = toFiniteNumber(slotData.used) ?? 0;
    if (total == null || total < 0 || used < 0 || used > total) return;

    target[level] = { total, used };
  });
};

const assignSpellcastingMetadata = (target, source) => {
  if (!source || typeof source !== 'object') return;

  const normalizedAbility = String(
    source.ability
    ?? source.spellcastingAbility
    ?? source.spellAbility
    ?? ''
  ).trim().toLowerCase();

  if (!target.ability && normalizedAbility) {
    const canonicalAbility = ({
      int: 'intelligence',
      intelligence: 'intelligence',
      wis: 'wisdom',
      wisdom: 'wisdom',
      cha: 'charisma',
      charisma: 'charisma'
    })[normalizedAbility];

    if (canonicalAbility) {
      target.ability = canonicalAbility;
    }
  }

  const attackBonus = toFiniteNumber(source.attackBonus ?? source.spellAttackBonus);
  if (target.attackBonus == null && attackBonus != null) {
    target.attackBonus = attackBonus;
  }

  const saveDC = toFiniteNumber(source.saveDC ?? source.saveDc ?? source.spellSaveDC);
  if (target.saveDC == null && saveDC != null) {
    target.saveDC = saveDC;
  }
};

const hasSpellLevelEntries = (byLevel) => (
  byLevel
  && typeof byLevel === 'object'
  && Object.values(byLevel).some((entries) => Array.isArray(entries) && entries.length > 0)
);

export const resolveCombatantSpellData = (...inputs) => {
  const sources = collectCombatantSources(...inputs);
  const normalized = {
    known: [],
    prepared: [],
    atWill: [],
    byLevel: {},
    slots: {}
  };

  const seenKnown = new Set();
  const seenPrepared = new Set();
  const seenAtWill = new Set();
  const seenByLevel = {};

  const mergeSpellPool = (pool) => {
    if (!pool || typeof pool !== 'object') return;

    assignSpellcastingMetadata(normalized, pool);

    mergeSpellArray(normalized.known, pool.known, null, seenKnown);
    mergeSpellArray(normalized.known, pool.spells, null, seenKnown);
    mergeSpellArray(normalized.known, pool.spellList, null, seenKnown);
    mergeSpellArray(normalized.known, pool.knownSpells, null, seenKnown);
    mergeSpellArray(normalized.prepared, pool.prepared, null, seenPrepared);
    mergeSpellArray(normalized.prepared, pool.preparedSpells, null, seenPrepared);
    mergeSpellArray(normalized.atWill, pool.atWill, null, seenAtWill);

    normalized.byLevel[0] = normalized.byLevel[0] || [];
    seenByLevel[0] = seenByLevel[0] || new Set();
    mergeSpellArray(normalized.byLevel[0], pool.cantrips, 0, seenByLevel[0]);

    SPELL_LEVEL_ARRAY_KEYS.forEach(([levelKeyA, levelKeyB, numericLevel]) => {
      normalized.byLevel[numericLevel] = normalized.byLevel[numericLevel] || [];
      seenByLevel[numericLevel] = seenByLevel[numericLevel] || new Set();
      mergeSpellArray(normalized.byLevel[numericLevel], pool[levelKeyA], numericLevel, seenByLevel[numericLevel]);
      if (levelKeyB !== levelKeyA) {
        mergeSpellArray(normalized.byLevel[numericLevel], pool[levelKeyB], numericLevel, seenByLevel[numericLevel]);
      }
    });

    mergeSpellLevelMap(normalized.byLevel, pool.byLevel, seenByLevel);
    mergeSpellLevelMap(normalized.byLevel, pool.known_spells, seenByLevel);
    mergeSpellLevelMap(normalized.byLevel, pool.knownSpells, seenByLevel);
    mergeSpellLevelMap(normalized.byLevel, pool.prepared_spells, seenByLevel);
    mergeSpellLevelMap(normalized.byLevel, pool.preparedSpells, seenByLevel);

    if (pool.alwaysPrepared && typeof pool.alwaysPrepared === 'object') {
      Object.values(pool.alwaysPrepared).forEach((spellList) => {
        normalized.prepared = normalized.prepared || [];
        mergeSpellArray(normalized.prepared, spellList, null, seenPrepared);
      });
    }

    if (pool.alwaysPreparedSpells && typeof pool.alwaysPreparedSpells === 'object') {
      Object.values(pool.alwaysPreparedSpells).forEach((spellList) => {
        normalized.prepared = normalized.prepared || [];
        mergeSpellArray(normalized.prepared, spellList, null, seenPrepared);
      });
    }

    if (pool.always_prepared_spells && typeof pool.always_prepared_spells === 'object') {
      Object.values(pool.always_prepared_spells).forEach((spellList) => {
        normalized.prepared = normalized.prepared || [];
        mergeSpellArray(normalized.prepared, spellList, null, seenPrepared);
      });
    }

    mergeSpellSlots(normalized.slots, pool.slots);
    mergeSpellSlots(normalized.slots, pool.spellSlots);
  };

  sources.forEach((source) => {
    assignSpellcastingMetadata(normalized, source);

    [
      asRecord(source?.spells),
      asRecord(source?.spellcasting),
      asRecord(source?.spellbook),
      asRecord(source?.data?.spells),
      asRecord(source?.data?.spellcasting)
    ].filter(Boolean).forEach(mergeSpellPool);

    mergeSpellArray(normalized.prepared, source?.preparedSpells, null, seenPrepared);
    mergeSpellArray(normalized.known, source?.spellList, null, seenKnown);
  });

  const hasAnyData = Boolean(
    normalized.ability
    || normalized.attackBonus != null
    || normalized.saveDC != null
    || normalized.known.length > 0
    || normalized.prepared.length > 0
    || normalized.atWill.length > 0
    || hasSpellLevelEntries(normalized.byLevel)
    || Object.keys(normalized.slots).length > 0
  );

  if (!hasAnyData) {
    return null;
  }

  if (normalized.known.length === 0) delete normalized.known;
  if (normalized.prepared.length === 0) delete normalized.prepared;
  if (normalized.atWill.length === 0) delete normalized.atWill;
  if (!hasSpellLevelEntries(normalized.byLevel)) delete normalized.byLevel;
  if (Object.keys(normalized.slots).length === 0) delete normalized.slots;

  return normalized;
};

export const resolveCombatantSpeedFeet = (...inputs) => {
  const sources = collectCombatantSources(...inputs);

  for (const source of sources) {
    const combatStats = asRecord(source.combatStats);
    const candidates = [
      source.speed,
      source.movement,
      source.speedModifiers,
      combatStats?.speed
    ];

    for (const candidate of candidates) {
      const parsed = parseSpeedFeet(candidate);
      if (parsed != null) {
        return parsed;
      }
    }
  }

  return null;
};

export const parseArmorClassValue = (value) => {
  if (value == null) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const match = value.match(/-?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value && typeof value === 'object') {
    return parseArmorClassValue(
      value.value
      ?? value.base
      ?? value.armorClass
      ?? value.ac
      ?? value.total
      ?? null
    );
  }

  return null;
};

export const resolveCombatantArmorClass = (...inputs) => {
  const sources = collectCombatantSources(...inputs);

  for (const source of sources) {
    const combatStats = asRecord(source.combatStats);
    const defense = asRecord(source.defense);
    const candidates = [
      source.ac,
      source.armorClass,
      combatStats?.armorClass,
      combatStats?.ac,
      defense?.ac
    ];

    for (const candidate of candidates) {
      const parsed = parseArmorClassValue(candidate);
      if (parsed != null && parsed >= 0) {
        return parsed;
      }
    }
  }

  return null;
};

export const resolveCombatantCurrentHp = (...inputs) => {
  const sources = collectCombatantSources(...inputs);

  for (const source of sources) {
    const hitPoints = asRecord(source.hitPoints);
    const defense = asRecord(source.defense);
    const defenseHp = asRecord(defense?.hp);
    const hpObject = asRecord(source.hpObj);
    const hpEnvelope = asRecord(source.hp);
    const candidates = [
      hpObject?.current,
      hitPoints?.current,
      hitPoints?.value,
      defenseHp?.current,
      hpEnvelope?.current,
      source.hp
    ];

    for (const candidate of candidates) {
      const parsed = toFiniteNumber(candidate);
      if (parsed != null) {
        return parsed;
      }
    }
  }

  return null;
};

export const resolveCombatantConditions = (...inputs) => {
  const sources = collectCombatantSources(...inputs);
  const resolved = new Set();

  const addCondition = (value) => {
    if (typeof value !== 'string') return;
    const normalized = value.trim().toLowerCase().replace(/_/g, ' ');
    if (normalized) {
      resolved.add(normalized);
    }
  };

  for (const source of sources) {
    if (Array.isArray(source.conditions)) {
      source.conditions.forEach(addCondition);
    }

    if (source.statusEffects && typeof source.statusEffects === 'object') {
      Object.entries(source.statusEffects).forEach(([key, value]) => {
        if (value) addCondition(key);
      });
    }

    if (source.condition && typeof source.condition === 'string') {
      addCondition(source.condition);
    }

    if (source.showKoMarker) {
      resolved.add('knocked out');
    }
  }

  return Array.from(resolved);
};

const INCAPACITATING_CONDITIONS = new Set([
  'dead',
  'defeated',
  'incapacitated',
  'knocked out',
  'paralyzed',
  'petrified',
  'stunned',
  'unconscious'
]);

export const getCombatantActionBlockReason = (...inputs) => {
  const currentHp = resolveCombatantCurrentHp(...inputs);
  if (currentHp != null && currentHp <= 0) {
    return 'This combatant is at 0 HP and cannot act.';
  }

  const blockingCondition = resolveCombatantConditions(...inputs)
    .find((condition) => INCAPACITATING_CONDITIONS.has(condition));

  if (blockingCondition) {
    return `This combatant is ${blockingCondition} and cannot act.`;
  }

  return null;
};

export const resolveCombatantInitiative = (...inputs) => {
  const sources = collectCombatantSources(...inputs);

  for (const source of sources) {
    const combatStats = asRecord(source.combatStats);
    const candidates = [
      source.initiative,
      combatStats?.initiative
    ];

    for (const candidate of candidates) {
      const numeric = toFiniteNumber(candidate);
      if (numeric != null) {
        return numeric;
      }
    }
  }

  return null;
};

export const resolveCombatantProficiencyBonus = (...inputs) => {
  const sources = collectCombatantSources(...inputs);

  for (const source of sources) {
    const combatStats = asRecord(source.combatStats);
    const directCandidates = [
      source.proficiencyBonus,
      combatStats?.proficiencyBonus
    ];

    for (const candidate of directCandidates) {
      const numeric = toFiniteNumber(candidate);
      if (numeric != null && numeric >= 0) {
        return numeric;
      }
    }

    const level = toFiniteNumber(source.level);
    if (level != null && level > 0) {
      return Math.ceil(level / 4) + 1;
    }

    const challengeCandidates = [
      source.challengeRating,
      source.cr,
      source.challenge
    ];

    for (const candidate of challengeCandidates) {
      const derived = deriveProficiencyBonusFromChallengeRating(candidate);
      if (derived != null) {
        return derived;
      }
    }
  }

  return null;
};

export const resolveCombatantChallengeDisplay = (...inputs) => {
  const sources = collectCombatantSources(...inputs);

  for (const source of sources) {
    const challengeCandidates = [
      source.challengeRating,
      source.cr,
      source.challenge
    ];

    for (const candidate of challengeCandidates) {
      if (candidate == null) continue;

      if (typeof candidate === 'object') {
        const nested = firstPresentValue(
          candidate.display,
          candidate.cr,
          candidate.rating,
          candidate.value
        );
        if (nested != null) {
          return nested;
        }
        continue;
      }

      return candidate;
    }
  }

  return null;
};

export const resolveAbilityScores = (...inputs) => {
  const sources = collectCombatantSources(...inputs);
  const resolved = {};

  for (const [canonicalKey, aliases] of Object.entries(ABILITY_KEY_MAP)) {
    for (const source of sources) {
      const candidateRecords = [
        asRecord(source?.abilities),
        asRecord(source?.stats),
        asRecord(source)
      ].filter(Boolean);

      let found = null;

      for (const record of candidateRecords) {
        for (const key of aliases) {
          const numeric = toFiniteNumber(record[key]);
          if (numeric != null) {
            found = numeric;
            break;
          }
        }

        if (found != null) {
          break;
        }
      }

      if (found != null) {
        resolved[canonicalKey] = found;
        break;
      }
    }
  }

  return resolved;
};

export const completeAbilityScores = (abilities, fallbackValue = null) => {
  const completed = {};

  Object.keys(ABILITY_KEY_MAP).forEach((key) => {
    const numeric = toFiniteNumber(abilities?.[key]);
    if (numeric != null) {
      completed[key] = numeric;
    } else if (fallbackValue != null) {
      completed[key] = fallbackValue;
    }
  });

  return completed;
};

export const buildShortAbilityBlock = (abilities, fallbackValue = null) => {
  const completed = completeAbilityScores(abilities, fallbackValue);
  if (Object.keys(completed).length === 0) {
    return null;
  }

  return {
    str: completed.strength,
    dex: completed.dexterity,
    con: completed.constitution,
    int: completed.intelligence,
    wis: completed.wisdom,
    cha: completed.charisma
  };
};

export const isUniformTenAbilityBlock = (abilities) => (
  Boolean(abilities)
  && Object.keys(ABILITY_KEY_MAP).every((key) => toFiniteNumber(abilities[key]) === 10)
);

export const hasExplicitRuntimeAbilityOverride = (source) => Boolean(
  source?.hasCustomAbilityScores
  || source?.abilityOverrides
  || source?.runtimeOverrides?.abilities
  || source?.sheetOverrides?.abilities
);

export const hasExplicitRuntimeArmorClassOverride = (source) => Boolean(
  source?.hasCustomArmorClass
  || source?.armorClassOverride
  || source?.acOverride
  || source?.runtimeOverrides?.ac
  || source?.runtimeOverrides?.armorClass
  || source?.sheetOverrides?.ac
  || source?.sheetOverrides?.armorClass
);

export const hasExplicitRuntimeInitiativeOverride = (source) => Boolean(
  source?.hasCustomInitiative
  || source?.initiativeOverride
  || source?.runtimeOverrides?.initiative
  || source?.sheetOverrides?.initiative
);

export const resolvePreferredAbilityScores = ({
  runtimeSources = [],
  canonicalSources = [],
  runtimeOverrideSource = null,
  fallbackValue = null
} = {}) => {
  const runtimeAbilities = resolveAbilityScores(runtimeSources);
  const canonicalAbilities = resolveAbilityScores(canonicalSources);
  const hasRuntimeAbilities = Object.keys(runtimeAbilities).length > 0;
  const hasCanonicalAbilities = Object.keys(canonicalAbilities).length > 0;

  if (!hasRuntimeAbilities) {
    return completeAbilityScores(canonicalAbilities, fallbackValue);
  }

  if (!hasCanonicalAbilities) {
    return completeAbilityScores(runtimeAbilities, fallbackValue);
  }

  if (hasExplicitRuntimeAbilityOverride(runtimeOverrideSource)) {
    return completeAbilityScores(runtimeAbilities, fallbackValue);
  }

  if (isUniformTenAbilityBlock(runtimeAbilities) && !isUniformTenAbilityBlock(canonicalAbilities)) {
    return completeAbilityScores(canonicalAbilities, fallbackValue);
  }

  return completeAbilityScores(runtimeAbilities, fallbackValue);
};

export const resolvePreferredArmorClass = ({
  runtimeSources = [],
  canonicalSources = [],
  runtimeOverrideSource = null,
  fallbackValue = null
} = {}) => {
  const runtimeArmorClass = resolveCombatantArmorClass(runtimeSources);
  const canonicalArmorClass = resolveCombatantArmorClass(canonicalSources);

  if (runtimeArmorClass == null) {
    return canonicalArmorClass ?? fallbackValue;
  }

  if (canonicalArmorClass == null) {
    return runtimeArmorClass;
  }

  if (hasExplicitRuntimeArmorClassOverride(runtimeOverrideSource)) {
    return runtimeArmorClass;
  }

  if (runtimeArmorClass === 10 && canonicalArmorClass !== 10) {
    return canonicalArmorClass;
  }

  return runtimeArmorClass;
};

export const resolvePreferredInitiative = ({
  runtimeSources = [],
  canonicalSources = [],
  runtimeOverrideSource = null,
  fallbackValue = null
} = {}) => {
  const runtimeInitiative = resolveCombatantInitiative(runtimeSources);
  const canonicalInitiative = resolveCombatantInitiative(canonicalSources);

  if (runtimeInitiative == null) {
    return canonicalInitiative ?? fallbackValue;
  }

  if (canonicalInitiative == null) {
    return runtimeInitiative;
  }

  if (hasExplicitRuntimeInitiativeOverride(runtimeOverrideSource)) {
    return runtimeInitiative;
  }

  if (runtimeInitiative === 0 && canonicalInitiative !== 0) {
    return canonicalInitiative;
  }

  return runtimeInitiative;
};
